import { CancelSessionOutput } from '@uplift/api';
import { useCancelSession } from '@uplift/queries';
import { l10nEnService } from '@uplift/services';
import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { boolean, mixed, object, ObjectSchema, string } from 'yup';
import { CancelFormModalCommonProps, CancelSessionForm, RecurringType } from './types';

export type UseCancelSessionFormArgs = {
  onSuccess: (response: CancelSessionOutput, form: CancelSessionForm) => Promise<void>;
  onError: () => void;
} & CancelFormModalCommonProps;

export const useCancelSessionForm = (args: UseCancelSessionFormArgs) => {
  const queryClient = useQueryClient();
  const cancelSession = useCancelSession();

  const onSubmit = useCallback(async (form: CancelSessionForm) => {
    try {
      const result = await cancelSession({
        sessionId: args.appointmentId,
        parentId: args.parentId,
        cancelReasonId: form.cancelationReason as string,
        recurring: form.recurring,
        chargeLate: form.chargeFee,
        fromDatetime: args.fromDatetime,
        cancellationNote: form.cancelNote,
      });

      if (result?.error) {
        throw new Error(result.error);
      }

      queryClient.invalidateQueries({ queryKey: [ 'calendar' ] });
      await args.onSuccess(result, form);
    } catch (error) {
      args.onError();
    }
  }, [ args, cancelSession, queryClient ]);

  const initialValues: CancelSessionForm = useMemo(() => ({
    appointmentId: args.appointmentId,
    parentId: args.parentId,
    cancelationReason: null,
    recurring: args.cancelMode === 'manual' ? 'single' : args.cancelMode || 'single',
    chargeFee: false,
  }), [ args.cancelMode, args.appointmentId, args.parentId ]);

  return {
    initialValues,
    onSubmit
  };
};


export const useCancelSessionFormValidationSchema = (l10n: typeof l10nEnService): ObjectSchema<CancelSessionForm> =>
  useMemo(() => object({
    cancelationReason: string()
      .nullable()
      .required(l10n.validations.requiredCancelationReason),
    recurring: mixed<RecurringType>()
      .oneOf([ 'single', 'all' ])
      .required(),
    chargeFee: boolean().required(),
    cancelNote: string()
  }), [ l10n.validations.requiredCancelationReason ]);
